import { FC, useEffect, useMemo, useRef } from "react";
import { Controller } from "react-hook-form";
import { format, parse } from "date-fns";
import Select, { GroupBase, MultiValue, OptionsOrGroups } from "react-select";
import { useDebouncedCallback } from "use-debounce";
import DatePicker from "react-datepicker";
import ReactInputMask from "react-input-mask";

import useFetch from "src/hooks/useFetch";
import HubInputField from "src/components/inputType";

import { APIEndpoints } from "src/types/apiTypes";
import {
  DiagnosisResponseProps,
  CaseStepOneProps,
  ProductRequestProps,
} from "./types";
import CurrentAndPriorDiuretics from "../addCase/currentAndPriorDiuretics";
import { CASE_REVIEW_TYPES_FOR_READYFLOW, CASE_REVIEW_TYPES_ON_BODY_INFUSOR, NYHA_CLASSES, FREQUENCY_TYPES, DAILY_DOSE_TYPES, CASE_REVIEW_TYPES } from "src/constants/cases";

const READYFLOW_DIRECTIONS =
  "Administer one 80mg single dose prefilled autoinjector PRN as directed";

const ON_BODY_INFUSOR_DIRECTIONS =
  "Administer 1 dose(80mg) subcutaneously via the On-Body infusor once daily as directed";

type ProductOption = {
  value: string;
  label: string;
};

const normalizeProductName = (name?: string) =>
  (name || "").toLowerCase().replace(/°/g, "").trim();

const isReadyFlowProduct = (name?: string) => {
  const productName = normalizeProductName(name);

  return (
    productName.includes("readyflow") ||
    productName.includes("ready flow") ||
    productName.includes("autoinjector")
  );
};

const isOnBodyInfusorProduct = (name?: string) => {
  const productName = normalizeProductName(name);

  return (
    productName.includes("on-body") ||
    productName.includes("on body") ||
    productName.includes("infusor")
  );
};

const CaseStepOne: FC<CaseStepOneProps> = ({
  control,
  isVisible,
  watch,
  setValue,
  getValues,
  resetField,
  iscloneCase
}): JSX.Element => {

  const drug_name = watch("prescription_attributes.drug_name");
  const diagnosisCode = watch("prescription_attributes.diagnosis_code");
  const currentReviewType = watch("prescription_attributes.review_type");
  const hasUsedFuroscix = watch("prescription_attributes.has_used_furoscix");
  const frequencyOfUse = watch("prescription_attributes.frequency_of_use");
  const dailyDose = watch("prescription_attributes.daily_dose");
  const reviewType = watch("prescription_attributes.review_type");

  const { data: productData } = useFetch<ProductRequestProps[]>(
    APIEndpoints.GET_PRODUCTS
  );

  // const productDropdownData = useMemo((): ProductOption[] => {
  //   if (!productData || productData.length === 0) return [];

  //   const readyFlowProduct = productData.find((product) =>
  //     isReadyFlowProduct(product.name)
  //   );

  //   const onBodyInfusorProduct = productData.find((product) =>
  //     isOnBodyInfusorProduct(product.name)
  //   );

  //   if (!readyFlowProduct) return [];

  //   const readyFlowOption: ProductOption = {
  //     value: readyFlowProduct.name,
  //     label: readyFlowProduct.name,
  //   };
   

  //   const onBodyInfusorOption: ProductOption | null = onBodyInfusorProduct
  //     ? {
  //       value: onBodyInfusorProduct.name,
  //       label: onBodyInfusorProduct.name,
  //     }
  //     : null;

  //   // If No: only ReadyFlow product will be available.
  //   // if (hasUsedFuroscix === false) {
  //   //   return [readyFlowOption];
  //   // }

  //   // If Yes: ReadyFlow first, then On-Body Infusor.
  //   if (hasUsedFuroscix === true && onBodyInfusorOption) {
  //     return [readyFlowOption, onBodyInfusorOption];
  //   }

  //   // Default before Yes/No selection: ReadyFlow only.
  //   return [readyFlowOption];
  // }, [productData, hasUsedFuroscix]);


  const productDropdownData = useMemo((): ProductOption[] => {
  if (!productData || productData.length === 0) {
    return [];
  }

  const readyFlowProduct = productData.find((product) =>
    isReadyFlowProduct(product.name)
  );

  const onBodyInfusorProduct = productData.find((product) =>
    isOnBodyInfusorProduct(product.name)
  );

  const products: ProductOption[] = [];

  if (readyFlowProduct) {
    products.push({
      value: readyFlowProduct.name,
      label: readyFlowProduct.name,
    });
  }

  if (onBodyInfusorProduct) {
    products.push({
      value: onBodyInfusorProduct.name,
      label: onBodyInfusorProduct.name,
    });
  }

  return products;
}, [productData]);
  //  useEffect((
      
  //   ) => {
  //     if (!iscloneCase) {
  //       setValue("prescription_attributes.review_type", "", {
  //   shouldValidate: true,
  //   shouldDirty: true,
  // });}
  //   }, [drug_name]);

  useEffect(() => {
    if (frequencyOfUse?.trim() !== "Other") {
      setValue("prescription_attributes.other_frequency_of_use", "", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [frequencyOfUse]);

  useEffect(() => {
    if (dailyDose !== "Other") {
      setValue("prescription_attributes.other_daily_dose", "", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [dailyDose])

  useEffect(() => {
    const selectedProduct = normalizeProductName(drug_name);

    if (!selectedProduct) return;

    if (isReadyFlowProduct(selectedProduct)) {
      setValue("prescription_attributes.direction", READYFLOW_DIRECTIONS, {
        shouldValidate: true,
        shouldDirty: true,
      });

      return;
    }

    if (isOnBodyInfusorProduct(selectedProduct)) {
      setValue(
        "prescription_attributes.direction",
        ON_BODY_INFUSOR_DIRECTIONS,
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
    }
    setValue(
        "prescription_attributes.frequency_of_use",
        "",
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
      setValue(
        "prescription_attributes.other_frequency_of_use",
        "",
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
      setValue(
        "prescription_attributes.daily_dose",
        "",
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
      setValue(
        "prescription_attributes.supervising_md_firstname",
        "",
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
      setValue(
        "prescription_attributes.supervising_md_lastname",
        "",
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
      

  }, [drug_name, setValue]);

  useEffect(() => {
    if (!currentReviewType) return;

    setValue(
      "prescription_attributes.quickstart",
      currentReviewType === "cp"
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentReviewType]);

  const {
    loading: diagnosisLoader,
    data: rfcListing,
    mutate: getDiagnosis,
    removeErrorAndData: clearDiagnosisData,
  } = useFetch<DiagnosisResponseProps>(APIEndpoints.GET_DIAGNOSIS_DROPDOWN);

  const searchDiagnosis = (search: string) => {
    if (typeof search === "string" && search.length >= 2) {
      clearDiagnosisData();
      getDiagnosis(null, null, { search });
    }
  };

  const debounced = useDebouncedCallback(searchDiagnosis, 1500);

  useEffect(() => {
    if (diagnosisCode || (rfcListing && rfcListing.data)) {
      setTimeout(() => clearDiagnosisData(), 150);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagnosisCode]);
  const previousHasUsedFuroscix = useRef<boolean | undefined>(
    hasUsedFuroscix
  );

  useEffect(() => {
    if (!iscloneCase) { // Do nothing when neither Yes nor No is selected.
      if (hasUsedFuroscix === undefined) {
        previousHasUsedFuroscix.current = undefined;
        return;
      }

      const hasAnswerChanged =
        previousHasUsedFuroscix.current !== hasUsedFuroscix;

      if (hasAnswerChanged && !hasUsedFuroscix && productDropdownData.length > 0) {
        const readyFlowProduct = productDropdownData.find((product) =>
          isReadyFlowProduct(product.label)
        );

        const onBodyInfusorProduct = productDropdownData.find((product) =>
          isOnBodyInfusorProduct(product.label)
        );
        console.log("hasAnswerChanged", hasAnswerChanged, hasUsedFuroscix);
        setValue(
          "prescription_attributes.drug_name",
          readyFlowProduct?.value ?? "",
          {
            shouldValidate: false,
            shouldDirty: true,
            shouldTouch: false,
          }
        );
        setValue("prescription_attributes.direction", READYFLOW_DIRECTIONS, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else if (hasAnswerChanged && hasUsedFuroscix && productDropdownData.length > 0) {
        setValue(
          "prescription_attributes.drug_name",
          "",
          {
            shouldValidate: false,
            shouldDirty: true,
            shouldTouch: false,
          }
        );
        setValue("prescription_attributes.direction", "", {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      previousHasUsedFuroscix.current = hasUsedFuroscix;
    }
  }, [hasUsedFuroscix, setValue]);






  /* DEV-4046 Client Remove the hasUsedFurocix  radio Button and now we proceding with the logic that hasUsedFuroscix===true
   <div className="col-lg-12">
                <Controller
                  name="prescription_attributes.has_used_furoscix"
                  control={control}
                  defaultValue={undefined}
                  render={({ field, fieldState: { error } }) => (
                    <div className="row mb-2">
                      <label
                        htmlFor="prescription_attributes.has_used_furoscix"
                        className="form-label col-lg-4 col-md-4 col-12 fw-semibold pe-0 justify-content-start"
                      >
                        Has the patient been on FUROSCIX in the past? *
                      </label>

                      <div className="col-lg-8 col-md-8 col-12">
                        <div className="d-flex gap-4 align-items-center">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              id="has_used_furoscix_yes"
                              className={`form-check-input ${error?.message ? "is-invalid" : ""
                                }`}
                              checked={field.value === true}
                              onChange={(e) => {
                                field.onChange(
                                  e.target.checked ? true : undefined
                                );
                              }}
                            />

                            <label
                              className="form-check-label fw-semibold"
                              htmlFor="has_used_furoscix_yes"
                            >
                              Yes
                            </label>
                          </div>

                          <div className="form-check">
                            <input
                              type="checkbox"
                              id="has_used_furoscix_no"
                              className={`form-check-input ${error?.message ? "is-invalid" : ""
                                }`}
                              checked={field.value === false}
                              onChange={(e) => {
                                field.onChange(
                                  e.target.checked ? false : undefined
                                );
                              }}
                            />

                            <label
                              className="form-check-label fw-semibold"
                              htmlFor="has_used_furoscix_no"
                            >
                              No
                            </label>
                          </div>
                        </div>

                        {error?.message ? (
                          <div className="text-danger text-small mt-2">
                            {error.message}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                />
              </div>*/ 
  return (
    <div className={!isVisible ? "d-none" : ""}>
      <div className="mb-3">
        <div className="card mb-3">
          <div className="card-header">
            <h1 className="m-0">Getting Started</h1>
          </div>

          <div className="card-body">
            <p>
              In order to process your request for benefit investigation, the
              following information must be completed. All information is kept
              confidential according to HIPAA guidelines. Please fill out the
              fields below as completely as possible. Missing fields may result
              in a delay to processing patient benefits in a timely manner.
            </p>

            <div className="row">
             

              <div className="col-lg-6">
                <Controller
                  name="prescription_attributes.drug_name"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      inputId="prescription_attributes.drug_name"
                      inputLabel="Product"
                      isRequired={true}
                      isRowBased={true}
                      isRowInsideAnother={true}
                      field={field}
                      error={error}
                      isDropdown={true}
                      isSearchable={false}
                      inputPlaceholder="Select a Product ..."
                      isTypeAhead
                      // dropdownData={
                      //   productDropdownData as unknown as OptionsOrGroups<
                      //     string,
                      //     GroupBase<string>
                      //   >
                      // }
                      dropdownData={
                        productDropdownData.map((item) => ({
                          id: item.value,
                          name: item.label,
                        })).map((procedure) => ({
                          value: procedure.id,
                          label: procedure.name,
                        })) as unknown as OptionsOrGroups<
                          string,
                          GroupBase<string>
                        >
                      }
                    />
                  )}
                />
              </div>

              <div className="col-12 col-md-12 col-lg-6">
                <Controller
                  name="prescription_attributes.condition"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      inputId="prescription_attributes.condition"
                      inputLabel="Condition"
                      isRequired={true}
                      isRowBased={true}
                      isRowInsideAnother={true}
                      field={field}
                      error={error}
                      isDropdown={true}
                      isSearchable={false}
                      inputPlaceholder="Specify Condition..."
                      isTypeAhead
                      dropdownData={
                        NYHA_CLASSES.map((item) => ({
                          id: item.id,
                          name: item.name,
                        })).map((procedure) => ({
                          value: procedure.id,
                          label: procedure.name,
                        })) as unknown as OptionsOrGroups<
                          string,
                          GroupBase<string>
                        >
                      }
                    />
                  )}
                />
              </div>

              <div className="col-12 col-md-12 col-lg-6">
                <Controller
                  name="prescription_attributes.review_type"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      inputId="prescription_attributes.review_type"
                      inputLabel="Review Type"
                      isRequired={true}
                      isRowBased={true}
                      isRowInsideAnother={true}
                      field={field}
                      error={error}
                      isDropdown={true}
                      isSearchable={false}
                      inputPlaceholder="Select a Review Type..."
                      isTypeAhead
                      dropdownData={
                          CASE_REVIEW_TYPES.map((item) => ({
                            id: item.id,
                            name: item.name,
                          })).map((procedure) => ({
                            value: procedure.id,
                            label: procedure.name,
                          })) as unknown as OptionsOrGroups<
                            string,
                            GroupBase<string>
                          > 
                      }
                    />
                  )}
                />
              </div>

              <div className="col-lg-6">
                <Controller
                  name="prescription_attributes.injection_site"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <div className="row mb-2">
                      <label
                        htmlFor="prescription_attributes.qty"
                        className="form-label col-lg-4 col-md-4 col-12"
                      >
                        Qty *
                      </label>

                      <div className="col-lg-8 col-md-8 col-12">
                        <div className="input-group">
                          <input
                            type="text"
                            id="prescription_attributes.qty"
                            style={{ zIndex: 0 }}
                            className={`form-control ${error && error.message ? "is-invalid" : ""
                              }`}
                            placeholder="Quantity"
                            {...field}
                          />

                          <span className="input-group-text" id="basic-addon2">
                            Doses
                          </span>
                        </div>

                        {error && error.message ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: error.message,
                            }}
                            className="text-danger text-small mt-2"
                          ></div>
                        ) : null}
                      </div>
                    </div>
                  )}
                />
              </div>

            </div>





            <div className="row">
              <div className="col-lg-6">
                <div className="row mb-2">
                  <label
                    htmlFor="prescription_attributes.diagnosis_code"
                    className="form-label col-lg-4 col-md-4 col-12"
                  >
                    Diagnosis Code *
                  </label>

                  <div className="col-lg-8 col-md-8 col-12">
                    <Controller
                      name="prescription_attributes.diagnosis_code"
                      control={control}
                      defaultValue={[]}
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <Select
                            options={
                              rfcListing &&
                                rfcListing.data &&
                                rfcListing.data.length > 0
                                ? (rfcListing.data.map((option) => ({
                                  value: option.code,
                                  label: option.fullname,
                                })) as unknown as OptionsOrGroups<
                                  string,
                                  GroupBase<string>
                                >)
                                : ([] as unknown as OptionsOrGroups<
                                  string,
                                  GroupBase<string>
                                >)
                            }
                            id="prescription_attributes.diagnosis_code"
                            isSearchable={true}
                            className={error ? "is-invalid" : ""}
                            styles={{
                              control: (baseStyles) => ({
                                ...baseStyles,
                                borderBottomRightRadius: 0,
                                borderTopRightRadius: 0,
                                borderColor: error
                                  ? "red"
                                  : baseStyles.borderColor,
                              }),
                            }}
                            isMulti={true}
                            placeholder="Enter diagnosis name"
                            {...field}
                            noOptionsMessage={() =>
                              diagnosisLoader
                                ? "Loading..."
                                : "Please enter 2 or more characters"
                            }
                            value={
                              field.value && field.value?.length > 0
                                ? field.value.map((item) => ({
                                  value: item,
                                  label: item,
                                }))
                                : ([] as unknown as MultiValue<unknown>)
                            }
                            onChange={(val) => {
                              field.onChange(
                                val.map(
                                  (item: unknown) =>
                                    (item as { value: string }).value
                                )
                              );
                            }}
                            onInputChange={debounced}
                          />

                          {error && error.message ? (
                            <div className="text-danger text-small mt-2">
                              {error.message}
                            </div>
                          ) : null}
                        </>
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <Controller
                  name="prescription_attributes.refills_authorized"
                  control={control}
                  defaultValue={0}
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      inputId="prescription_attributes.refills_authorized"
                      isRowBased={true}
                      isRowInsideAnother={true}
                      isRequired={false}
                      field={field}
                      inputLabel="Refills"
                      error={error}
                    />
                  )}
                />
              </div>


            </div>

            {drug_name === "Furoscix ReadyFlow Autoinjector" && (
              <div className="row">
                <div className="col-lg-6">
                  <Controller
                    name="prescription_attributes.frequency_of_use"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="prescription_attributes.frequency_of_use"
                        inputLabel="Frequency"
                        isRequired={true}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                        isDropdown={true}
                        isSearchable={false}
                        inputPlaceholder="Select a Frequency..."
                        isTypeAhead
                        dropdownData={
                          FREQUENCY_TYPES.map((item) => ({
                            id: item.id,
                            name: item.name,
                          })).map((procedure) => ({
                            value: procedure.id,
                            label: procedure.name,
                          })) as unknown as OptionsOrGroups<
                            string,
                            GroupBase<string>
                          >
                        }
                      />
                    )}
                  />
                  {frequencyOfUse === "Other" && (
                    <Controller
                      name="prescription_attributes.other_frequency_of_use"
                      control={control}
                      // defaultValue=""
                      render={({ field, fieldState: { error } }) => (
                        <HubInputField
                          inputId="prescription_attributes.other_frequency_of_use"
                          isRowBased={true}
                          isRowInsideAnother={true}
                          isRequired={true}
                          field={field}
                          inputLabel="Other Frequency"
                          inputPlaceholder="Enter Other Frequency "
                          error={error}
                        />
                      )}
                    />
                  )}
                </div>
                <div className="col-lg-6">

                  <Controller
                    name="prescription_attributes.daily_dose"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="prescription_attributes.daily_dose"
                        inputLabel="Daily Dose"
                        isRequired={true}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                        isDropdown={true}
                        isSearchable={false}
                        inputPlaceholder="Select a Daily Dose..."
                        isTypeAhead
                        dropdownData={
                          DAILY_DOSE_TYPES.map((item) => ({
                            id: item.id,
                            name: item.name,
                          })).map((procedure) => ({
                            value: procedure.id,
                            label: procedure.name,
                          })) as unknown as OptionsOrGroups<
                            string,
                            GroupBase<string>
                          >
                        }
                      />
                    )}
                  />
                  {dailyDose === "Other" && (
                    <Controller
                      name="prescription_attributes.other_daily_dose"
                      control={control}
                      defaultValue=""
                      render={({ field, fieldState: { error } }) => (
                        <HubInputField
                          inputId="prescription_attributes.other_daily_dose"
                          isRowBased={true}
                          isRowInsideAnother={true}
                          isRequired={true}
                          field={field}
                          inputLabel=" Other Daily Dose"
                          inputPlaceholder="Enter Other Daily Dose "
                          error={error}
                        />
                      )}
                    />
                  )}
                </div>


              </div>)}
            <div className="row">
              <div className="col-lg-6">
                <Controller
                  name="prescription_attributes.scheduled_date_of_service"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <div className="row align-items-center mb-3">
                      <label
                        htmlFor="patient_attributes.dob"
                        className="col-lg-4 col-md-4 col-12 form-label text-end mb-0 pe-0"
                      >
                        Anticipated Date of Service
                      </label>

                      <div className="col-lg-8 col-md-8 col-12 position-relative">
                        <DatePicker
                          selected={
                            field.value
                              ? parse(field.value, "yyyy-MM-dd", new Date())
                              : null
                          }
                          onChange={(date) => {
                            field.onChange(
                              date ? format(date, "yyyy-MM-dd") : ""
                            );
                          }}
                          dateFormat="MM/dd/yyyy"
                          className={`form-control ${error?.message ? "is-invalid" : ""
                            }`}
                          placeholderText="mm/dd/yyyy"
                          showMonthDropdown
                          showYearDropdown
                          customInput={
                            <ReactInputMask mask="99/99/9999">
                              {(inputProps: {}) => <input {...inputProps} />}
                            </ReactInputMask>
                          }
                          wrapperClassName="w-100"
                          minDate={new Date()}
                        />

                        <i
                          className="bi bi-calendar position-absolute"
                          style={{
                            right: error?.message ? "50px" : "25px",
                            top: "18px",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                          }}
                        ></i>

                        {error && error.message ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: error.message,
                            }}
                            className="text-danger text-small mt-2"
                          ></div>
                        ) : null}
                      </div>
                    </div>
                  )}
                />
              </div>

              <div className="col-lg-6">
                <Controller
                  name="prescription_attributes.direction"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      inputId="prescription_attributes.direction"
                      isRowBased={true}
                      isRowInsideAnother={true}
                      isRequired={false}
                      field={field}
                      inputLabel="Directions"
                      error={error}
                    />
                  )}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-lg-6">
                <Controller
                  name="prescription_attributes.note"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      inputId="prescription_attributes.note"
                      isRowBased={true}
                      isRowInsideAnother={true}
                      isRequired={false}
                      field={field}
                      inputLabel="Notes"
                      inputPlaceholder="Note Shared With Patient"
                      error={error}
                    />
                  )}
                />
              </div>

              <div className="col-lg-6">
                {drug_name === "Furoscix ReadyFlow Autoinjector" &&
                  <div className="row">
                    <label className="col-lg-4 col-md-4 col-12 form-label text-end mb-0 pe-0">Supervising MD</label>
                    <div className="col-lg-4 col-md-4 col-12">
                      <Controller
                        name="prescription_attributes.supervising_md_firstname"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState: { error } }) => (
                          <HubInputField
                            inputId="prescription_attributes.supervising_md_firstname"
                            isRowBased={true}
                            isRowInsideAnother={true}
                            isRequired={false}
                            field={field}
                            inputPlaceholder="First Name"
                            error={error}
                          />
                        )}
                      />
                    </div>
                    <div className="col-lg-4 col-md-4 col-12">
                      <Controller
                        name="prescription_attributes.supervising_md_lastname"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState: { error } }) => (
                          <HubInputField
                            inputId="prescription_attributes.supervising_md_lastname"
                            isRowBased={true}
                            isRowInsideAnother={true}
                            isRequired={false}
                            field={field}
                            inputPlaceholder="Last Name"
                            error={error}
                          />
                        )}
                      />
                    </div>
                  </div>}
              </div>

            </div>

            <Controller
              name="prescription_attributes.quickstart"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <div className="row form-check d-flex mb-3">
                  <label className="form-label col-12 col-md-4 col-lg-2"></label>

                  <div className="col-12 col-md-8 col-lg-10">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="prescription_attributes.quickstart"
                      disabled
                      checked={field.value}
                    />

                    <label
                      className="form-check-label"
                      htmlFor="prescription_attributes.quickstart"
                    >
                      I, the undersigned Prescriber, hereby authorize the
                      delivery of up to one free dose of FUROSCIX to the
                      patient’s address for next-day delivery, if eligible.
                    </label>
                  </div>
                </div>
              )}
            />

            <Controller
              name="prescription_attributes.written_date"
              control={control}
              defaultValue={format(new Date(), "MM/dd/yyyy")}
              render={({ field: { value } }) => (
                <div className="row form-check d-flex">
                  <label className="form-label col-12 col-md-4 col-lg-2"></label>

                  <div className="col-12 col-md-8 col-lg-10">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="flexCheckIndeterminateDisabled"
                      disabled
                      checked
                    />

                    <label
                      className="form-check-label"
                      htmlFor="flexCheckIndeterminateDisabled"
                    >
                      Dispense as Written (written date will be {value})
                    </label>
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header">
          <h1 className="m-0">Current and Prior Diuretics</h1>
        </div>

        <div className="card-body">
          <CurrentAndPriorDiuretics
            control={control}
            watch={watch}
            setValue={setValue}
            getValues={getValues}
            resetField={resetField}
          />
        </div>
      </div>
    </div>
  );
};

export default CaseStepOne;